import { isEmpty } from 'lodash';

import { ShipmentCollectionFilter } from '@/hooks/use-shipment-collection';
import { SavedItemType } from '@/lib/models/saved-item/types';
import { ShipmentVehicleModel } from '@/lib/models/shipment/types';
import { DEFAULT_SCHEDULE_SEARCH_PARAMS } from '@/state/standalone-schedules';
import {
  AdvancedSearchParams,
  AutocompletePlaceTypes,
  LocationInterface,
  PortsUrlQueryParams,
  RouteApiQueryParams,
  ScheduleSearchParamsInterface,
  ServicesApiParams,
  SpotRatesSearchParams,
  StandaloneScheduleSearchParams,
} from '@/types';
import { EmissionsCalculatorRequestParams, MarketRatesRequestParams } from '@/types/api-request-types';
import { SegmentModeType } from '@/types/api-types';
import { OrgsPublicTableInterface, UsersPublicTableInterface } from '@/types/database-types';
import { Fluent } from '@/utils/environment';
import { formatDateRangeForUrl } from '@/utils/format/date-time';
import { createQueryString } from '@/utils/helpers';
import { getLocationPortNodeId } from '@/utils/helpers/get-location-port-id';
import { formatPreferredIdentifier } from '@/utils/helpers/get-preferred-identifier';

import { getLocationApiParam } from '../get-location-api-param';

const { FLUENT_API_URL } = Fluent.env;

/**
 * Creates a url for the carriers search API
 */
export const createCarrierSearchUrl = (searchString: string, type?: SegmentModeType, engine = ''): string => {
  return `${FLUENT_API_URL}/carriers?q=${searchString}${type ? `&type=${type}` : ''}${engine ? `&engine=${engine}` : ''}`;
};

/**
 * Creates a url for the places search API
 */
export const createPlacesSearchUrl = (
  searchString: string,
  placeTypes?: AutocompletePlaceTypes,
  engine = '',
): string => {
  return `${FLUENT_API_URL}/places?q=${searchString}${placeTypes?.length ? `&placeTypes=${placeTypes.join(',')}` : ''}${engine ? `&engine=${engine}` : ''}`;
};

/**
 * Creates a url for the place details API
 * @param placeId - place to lookup
 * @param placeName - name fallback
 */
export const createPlaceDetailsSearchUrl = (placeId: string, placeName: string): string => {
  return `${FLUENT_API_URL}/place-details?placeId=${placeId}&string=${placeName}`;
};

/**
 * Creates a url for the vehicles search API
 */
export const createVehicleSearchUrl = (searchString: string, type?: SegmentModeType, engine = ''): string => {
  return `${FLUENT_API_URL}/vehicles?q=${searchString}${type ? `&type=${type}` : ''}${engine ? `&engine=${engine}` : ''}`;
};

/**
 * Creates a url for the Qayaq Route Search API endpoint
 */
export const createRouteSearchParams = (
  originLocation: string | LocationInterface,
  destinationLocation: string | LocationInterface,
  features?: string,
): RouteApiQueryParams => {
  // If a nodeId or Node Location (Seaport or Airport) is provided, just send the identifier
  const oId = getLocationPortNodeId(originLocation);
  const dId = getLocationPortNodeId(destinationLocation);

  // Else handle sending a Location Param Object
  const originQuery = oId ? { oId } : { oLocation: getLocationApiParam(originLocation) };
  const destinationQuery = dId ? { dId } : { dLocation: getLocationApiParam(destinationLocation) };

  return {
    ...originQuery,
    ...destinationQuery,
    features: features || '',
  };
};

/**
 * Returns a fully formed Route Query String
 */
export const createRouteSearchUrl = (
  origin?: LocationInterface | null,
  destination?: LocationInterface | null,
  advancedSearchParams: AdvancedSearchParams | null = null,
) => {
  if (!origin || !destination) return null;
  const query = createQueryString({
    o: origin.name,
    d: destination.name,
    ...advancedSearchParams,
  });

  return `${getRouteSearchUrl()}${query}`;
};

/**
 * Returns a fully formed Route Query String
 */
export const getRouteSearchUrl = () => `${FLUENT_API_URL}/routes`;

/**
 * Returns a fully Schedules Url String
 */
export const createStandaloneScheduleUrl = (
  params: Partial<StandaloneScheduleSearchParams>,
  isEnabled = true,
): string | null => {
  if (!isEnabled) return null;

  const { origin, destination, mode, dateFrom, dateTo, carrier, aircraftType, serviceType, features, lineSearch } = {
    ...DEFAULT_SCHEDULE_SEARCH_PARAMS,
    ...params,
  };
  if (!origin || !destination || !mode) return null;

  // Build query string of extra params to send
  const queryString = createQueryString({
    pol: formatPreferredIdentifier(origin.identifier),
    pod: formatPreferredIdentifier(destination.identifier),
    mode,
    dateFrom,
    dateTo,
    carrier,
    aircraftType,
    serviceType,
    features: features || '',
    lineSearch: lineSearch || '',
  });

  return `${getStandaloneSchedulesUrl()}${queryString}`;
};

/**
 * Returns a fully formed Schedule Query String
 * @param params
 * @param shouldUseLimited - will use the "limited" api endpoint to fetch a restricted
 * subset of schedules
 */
export const createScheduleSearchUrl = (params: ScheduleSearchParamsInterface, shouldUseLimited = false): string => {
  const { fromCode, toCode, mode, lineQuery, dateRange, arriveBy, aircraft, departHour, arriveHour } = params;
  // Build query string of extra params to send
  const queryString = createQueryString({
    o: fromCode,
    d: toCode,
    mode,
    arriveBy: arriveBy || false,
    dateRange: dateRange ? formatDateRangeForUrl({ dateRange, departHour, arriveHour }) : false,
    lines: lineQuery ? JSON.stringify(lineQuery) : false,
    ...(aircraft || {}),
  });

  return `${getSchedulesUrl(shouldUseLimited)}${queryString}`;
};

export const createSpotRatesSearchUrl = (params: Partial<SpotRatesSearchParams>) => {
  const { pol, pod, mode, dateFrom, dateTo } = {
    ...params,
  };
  if (!pol || !pod || !mode) return null;

  const queryString = createQueryString({
    pol,
    pod,
    mode,
    dateFrom: dateFrom || '',
    dateTo: dateTo || '',
  });

  return `${getSpotRatesUrl()}${queryString}`;
};

export const createMarketRatesSearchUrl = (params: Partial<MarketRatesRequestParams>) => {
  const { ports, mode, dateFrom, dateTo, additionalFields } = {
    ...params,
  };
  if (!ports || !mode) return null;

  const queryString = createQueryString({
    ports: JSON.stringify(ports),
    mode,
    dateFrom: dateFrom || '',
    dateTo: dateTo || '',
    additionalFields: additionalFields ? JSON.stringify(additionalFields) : '',
  });

  return `${getMarketRatesUrl()}${queryString}`;
};

export const getUsageUrl = () => `${FLUENT_API_URL}/usage`;

export const getSubscriptionInvoicesUrl = () => `${FLUENT_API_URL}/subscription/invoices`;

export const getSubscriptionUrl = (id?: string) => {
  return `${FLUENT_API_URL}/subscription${id ? `/${id}` : ''}`;
};

export const getPlansUrl = (getAll?: boolean) => {
  return `${FLUENT_API_URL}/subscription/plans${getAll ? '?getAll=true' : ''}`;
};

export const getSubscriptionPlanUrl = (planId: string) => `${getPlansUrl()}/${planId}`;

export const getPlanAddonsUrl = () => `${FLUENT_API_URL}/subscription/addons`;

export const getPlanAddonsPurchasesUrl = () => `${getPlanAddonsUrl()}/purchases`;

export const getFlightServicesUrl = () => `${FLUENT_API_URL}/services`;

export const getPortsUrl = (params?: PortsUrlQueryParams) =>
  !params ? `${FLUENT_API_URL}/ports` : `${FLUENT_API_URL}/ports${createQueryString(params as Record<string, string>)}`;

export const getSchedulesUrl = (shouldUseLimited?: boolean) =>
  `${FLUENT_API_URL}/schedules${shouldUseLimited ? '/limited' : ''}`;

export const getStandaloneSchedulesUrl = () => `${FLUENT_API_URL}/schedules/search`;

export const getOrgRootUrl = () => {
  return `${FLUENT_API_URL}/orgs`;
};

export const getOrgUrl = (orgId: OrgsPublicTableInterface['id'], includeUsage = false) => {
  if (!orgId) {
    return null;
  }

  return `${FLUENT_API_URL}/orgs/${orgId}${includeUsage ? '/usage' : ''}`;
};

export const getOrgUsageEventsUrl = (orgId: OrgsPublicTableInterface['id']) => {
  if (!orgId) {
    return null;
  }
  return `${getOrgUrl(orgId)}/usage/events`;
};

export const getOrgUsersUrl = (orgId: OrgsPublicTableInterface['id']) => {
  if (!orgId) {
    return null;
  }

  return `${getOrgUrl(orgId)}/users`;
};

export const getOrgUsersUserUrl = (orgId: OrgsPublicTableInterface['id'], userId: UsersPublicTableInterface['id']) => {
  if (!orgId || !userId) {
    return null;
  }

  return `${getOrgUrl(orgId)}/users/${userId}`;
};

export const getOrgsUsersInvitesUrl = (orgId: OrgsPublicTableInterface['id'] | string) => {
  return `${getOrgUrl(orgId)}/users/invite`;
};

export const getOrgTeamsUrl = (orgId: OrgsPublicTableInterface['id']) => {
  if (!orgId) {
    return null;
  }

  return `${getOrgUrl(orgId)}/teams`;
};

export const getInvitesUrl = () => {
  return `${FLUENT_API_URL}/orgs/invites`;
};

export const getQuotesAdminUrl = (pageIndex: number, perPage: number, statuses: string[]) => {
  return `${FLUENT_API_URL}/quotes?pageIndex=${pageIndex}&perPage=${perPage}&statuses=${statuses}`;
};

export const getOrgsAdminUrl = (
  pageIndex: number,
  perPage: number,
  subscriptionStatus: string,
  orgQuery: string,
  orgQueryType: string,
  includeChildOrgs: boolean,
) => {
  const orgSearchParams = {
    orgName: orgQueryType === 'name' ? orgQuery : '',
    orgId: orgQueryType === 'orgId' ? orgQuery : '',
  };

  const query = createQueryString({
    pageIndex,
    perPage,
    subscriptionStatus,
    includeChildOrgs,
    ...orgSearchParams,
  });

  return `${FLUENT_API_URL}/orgs${query}`;
};

export const getShipmentsAdminUrl = (pageIndex: number, perPage: number, statuses: string[]) => {
  return `${FLUENT_API_URL}/shipments/admin?pageIndex=${pageIndex}&perPage=${perPage}&statuses=${statuses}`;
};

export const getEmissionsCalculationUrl = () => `${FLUENT_API_URL}/emissions/calculator`;

export const getEmissionsReportsUrl = () => `${FLUENT_API_URL}/emissions/reports`;

export const getEmissionsReportUrl = (id?: string) => `${FLUENT_API_URL}/emissions/reports${id ? `/${id}` : ''}`;

export const createEmissionsCalculationUrl = (params: EmissionsCalculatorRequestParams) => {
  const { segments, cargo } = {
    ...params,
  };
  if (!segments || !cargo) return null;

  return `${getEmissionsCalculationUrl()}?segments=${JSON.stringify(segments)}&cargo=${JSON.stringify(cargo)}`;
};

export const getSaveEmissionsReportUrl = (params: { id?: string }) => {
  const { id } = params;
  const query = id ? createQueryString({ id }) : '';
  return `${getEmissionsReportsUrl()}${query}`;
};

export const getSpotRatesUrl = () => `${FLUENT_API_URL}/rates/live`;

export const getMarketRatesUrl = () => `${FLUENT_API_URL}/rates/market`;

export const getTotalUsersCountUrl = () => {
  return `${FLUENT_API_URL}/users/count`;
};

export const getImpersonateUserUrl = () => {
  return `${FLUENT_API_URL}/users/impersonate`;
};

export const getXenetaUsageUrl = () => {
  return `${FLUENT_API_URL}/logs/xeneta/usage`;
};

export const getXenetaUsageByDateRangeUrl = ({ dateFrom, dateTo }: { dateFrom?: Date; dateTo?: Date }) => {
  const query = createQueryString({ dateFrom: dateFrom?.toISOString() || '', dateTo: dateTo?.toISOString() || '' });

  return `${getXenetaUsageUrl()}${query ? `${query}` : ''}`;
};

export const getRevenueTotalsUrl = () => {
  return `${FLUENT_API_URL}/subscription/revenue`;
};

export const getDisruptionAlertsUrl = () => {
  return `${FLUENT_API_URL}/disruption-alerts`;
};

export const getFlightServicesUrlWithParams = (params: ServicesApiParams) => {
  const routesString = JSON.stringify(params.routes);
  const query = createQueryString({ ...params, routes: routesString });

  return `${getFlightServicesUrl()}${query}`;
};

export const getAllCarriersUrl = (hasTracking?: boolean) => {
  const query = createQueryString({ hasTracking: hasTracking !== undefined ? hasTracking : false });
  return `${FLUENT_API_URL}/carriers/all${query}`;
};

export const getUldsUrl = () => {
  return `${FLUENT_API_URL}/uld`;
};

export const getSearchHistoryUrl = (perPage = 10, page = 0) => {
  return `${FLUENT_API_URL}/usage/history?perPage=${perPage}&page=${page}`;
};

export const getPathsApiUrl = (ids: string[]) => {
  return `${FLUENT_API_URL}/paths?ids=${ids.join(',')}`;
};

export const getMailingListUrl = () => {
  return `${FLUENT_API_URL}/mailing-list`;
};

export const getContactFormEnquiryUrl = () => {
  return `${FLUENT_API_URL}/contact-form`;
};

export const getShipmentsUrl = () => {
  return `${FLUENT_API_URL}/shipments`;
};

export const getMyShipmentsUrl = () => {
  return `${FLUENT_API_URL}/shipments/me`;
};

export const getShipmentDetailUrl = (id: string) => {
  return `${getShipmentsUrl()}/${id}`;
};

export const getShipmentDocumentsUrl = (id: string) => {
  return `${getShipmentDetailUrl(id)}/documents`;
};

export const getShipmentDocumentsUploadUrl = (id: string) => {
  return `${getShipmentDocumentsUrl(id)}/upload`;
};

export const getShipmentHistoryUrl = (id: string) => {
  return `${getShipmentDetailUrl(id)}/history`;
};

export const getShipmentFiltersUrl = (sharedItemId?: string) => {
  if (sharedItemId) {
    return `${getSharedItemsShipmentsUrl(sharedItemId)}/filters`;
  }

  return `${getShipmentsUrl()}/filters`;
};

export const getShipmentsUpdateUrl = (shipmentId: string) => {
  return `${getShipmentDetailUrl(shipmentId)}/update`;
};

export const getShipmentTagsUrl = (shipmentId: string) => {
  return `${getShipmentDetailUrl(shipmentId)}/tags`;
};

export const getShipmentCollectionUrl = (collectionFilter: ShipmentCollectionFilter) => {
  return `${getShipmentsUrl()}/collection?${new URLSearchParams(collectionFilter)}`;
};

export const getShipmentsCountsUrl = () => {
  return `${getShipmentsUrl()}/count`;
};

export const getShipmentsDebugUrl = (trackingNumber?: string) => {
  return `${getShipmentsUrl()}/debug?trackingNumber=${trackingNumber}`;
};

export const getShipmentsDebugOrgsUrl = (name?: string) => {
  return `${getShipmentsUrl()}/debug/orgs${name ? `?name=${name}` : ''}`;
};

export const getShipmentsDebugUsersUrl = (orgId: string) => {
  return `${getShipmentsUrl()}/debug/users?orgId=${orgId}`;
};

export const getCreateOrgShipmentsDebugUrl = () => {
  return `${getShipmentsUrl()}/debug/org-shipment`;
};

export const getUserSettingsUrl = () => `${FLUENT_API_URL}/user-settings`;

export const getUserUrl = (userId: string) => {
  return `${FLUENT_API_URL}/users/${userId}`;
};

export const getSearchUsersUrl = (username: string, type = 'email') => {
  if (!username) return null;

  const query = createQueryString({
    email: type === 'email' ? username : '',
    userId: type === 'userId' ? username : '',
  });

  return `${FLUENT_API_URL}/users/search${query}`;
};

export const getInternalAuthUrl = () => `http://localhost:6006/api/auth`;

export const getVesselLocationsUrl = (vehicles: ShipmentVehicleModel[]) => {
  if (isEmpty(vehicles)) {
    return null;
  }
  const vehicleKeys = vehicles.map((vehicle) => vehicle.name || '');
  return `${getShipmentsUrl()}/vessel-locations?vehicles=${vehicleKeys.join(',')}`;
};

export const getSavedItemsUrl = (id?: string, types?: SavedItemType[]) => {
  const query = createQueryString({ types: (types || []).join(',') });
  const base = `${FLUENT_API_URL}/saved-items${query || ''}`;

  if (id) {
    return `${base}/${id}`;
  }

  return base;
};

export const getShipmentTableSettingsUrl = () => {
  return `${getSavedItemsUrl()}/shipment-table-settings`;
};

export const getSharedItemsUrl = (id?: string) => {
  const base = `${FLUENT_API_URL}/shared-items`;
  if (id) {
    return `${base}/${id}`;
  }

  return base;
};

export const getSharedItemsFindUrl = () => {
  return `${getSharedItemsUrl()}/find`;
};

export const getSharedItemsShipmentsUrl = (id: string) => {
  return `${getSharedItemsUrl()}/${id}/shipments`;
};

export const getSharedItemsShipmentEmissionsUrl = (id: string) => {
  return `${getSharedItemsUrl(id)}/emissions`;
};

export const getTagsUrl = (id?: string) => {
  const base = `${FLUENT_API_URL}/tags`;
  if (id) {
    return `${base}/${id}`;
  }

  return base;
};

export const getTrackingRequestsUrl = (id?: string, shouldUpdate: boolean = true) => {
  const base = `${FLUENT_API_URL}/tracking-requests`;
  if (id) {
    return `${base}/${id}?shouldUpdate=${shouldUpdate}`;
  }

  return base;
};

export const getTrackingRequestsBatchUrl = () => {
  return `${FLUENT_API_URL}/tracking-requests/batch`;
};

export const getTrackingRequestsProcessUrl = (id: string) => {
  return `${getTrackingRequestsUrl()}/${id}/process`;
};

export const getSavedShipmentViewsUrl = (id?: string) => {
  const base = `${FLUENT_API_URL}/saved-shipment-views`;
  if (id) {
    return `${base}/${id}`;
  }

  return base;
};

export const getUserNotificationsUrl = () => {
  return `${FLUENT_API_URL}/user-notifications`;
};

export const getUserNotificationReadUrl = (id: string) => {
  return `${getUserNotificationsUrl()}/${id}`;
};

export const getUserNotificationAllReadUrl = () => {
  return `${getUserNotificationsUrl()}/all-read`;
};

export const getVesselInsightsUrl = ({ nodeId, id }: { nodeId?: string; id?: string }) => {
  const base = `${FLUENT_API_URL}/insights/vehicles`;
  if (id) {
    return `${base}/${nodeId}/${id}`;
  }
  return base;
};

export { getMailCarriersUrl, getSyncMailCarriersUrl, updateMailCarrierImage } from './mail-carriers';

export const getWebhooksListUrl = () => `${FLUENT_API_URL}/orgs/webhooks`;
export const getWebhooksSingleUrl = (id: string) => `${getWebhooksListUrl()}/${id}`;

export const getWebhookNotificationsProcessUrl = (id: string) =>
  `${FLUENT_API_URL}/webhook-notifications/${id}/process`;

export const getWebhookLogsUrl = (webhookNotificationId: string) =>
  `${FLUENT_API_URL}/orgs/webhooks/${webhookNotificationId}/logs`;

export const getDocumentsUrl = (id?: string) => {
  const base = `${FLUENT_API_URL}/documents`;
  if (id) {
    return `${base}/${id}`;
  }

  return base;
};

export const getFileSignedUrl = () => {
  return `${FLUENT_API_URL}/files/get-signed-url`;
};

export const getTrackingRequestsAdminUrl = () => {
  return `${FLUENT_API_URL}/tracking-requests/admin`;
};

export const getShipmentDeletionRequestUrl = (shipmentId: string) => {
  return `${getShipmentDetailUrl(shipmentId)}/delete-request`;
};

export const getShipmentDeletionRequestsAdminUrl = (id?: string) => {
  const base = `${FLUENT_API_URL}/shipment-deletion-requests`;
  if (id) {
    return `${base}/${id}`;
  }

  return base;
};
